import React from 'react'
import createThrottle from 'utils/createThrottle'
import useGalleryContext from './useGalleryContext'
import { useDisplayVariant } from 'modules/productDetail'
import * as t from '../types'

type Gallery = {
  index: number
  totalThumbs: number
  slideNext: () => void
  slidePrev: () => void
  setIndex: (i: number) => void
  showSlideNext: boolean
  showSlidePrev: boolean
  thumbnails: t.Thumbnails
  mainImage: t.MainImage
  thumbnailRef: React.RefObject<HTMLDivElement | null> | null
}

export default function useGallery() {
  const images = useDisplayVariant().data.images
  const galleryContext = useGalleryContext()
  const thumbnailRef = React.useRef<HTMLDivElement | null>(null)
  const { index, totalThumbs, mainImage, thumbnails, setIndex } = galleryContext

  const [showSlideNext, setShowSlideNext] = React.useState<boolean>(false)
  const [showSlidePrev, setShowSlidePrev] = React.useState<boolean>(false)

  // Thumbnail Positioning
  // Scroll distance from start
  const [scrollTop, setScrollTop] = React.useState<number>(0)
  const [scrollLeft, setScrollLeft] = React.useState<number>(0)

  // Scroll container height and width (whole container, not only the visible part)
  const [scrollContainerHeight, setScrollContainerHeight] =
    React.useState<number>(0)
  const [scrollContainerWidth, setScrollContainerWidth] =
    React.useState<number>(0)
  // scroll container viewed height and width (the visible part)
  const [scrollVisibleHeight, setScrollVisibleHeight] =
    React.useState<number>(0)
  const [scrollVisibleWidth, setScrollVisibleWidth] = React.useState<number>(0)

  // thumbnail direction (vertical or horizontal)
  const thumbnailDirection = () => {
    if (scrollContainerWidth === scrollVisibleWidth) return 'vertical' as const
    return 'horizontal' as const
  }

  const slideNext = () => {
    if (thumbnailRef?.current) {
      const thumbRef = thumbnailRef.current
      if (thumbnailDirection() === 'vertical') {
        const newScrollTop = scrollTop + scrollVisibleHeight

        thumbRef.scrollTo({
          top: newScrollTop,
          behavior: 'smooth'
        })

        if (newScrollTop >= scrollContainerHeight - scrollVisibleHeight)
          setShowSlideNext(false)
        else setShowSlidePrev(true)
      }
      if (thumbnailDirection() === 'horizontal') {
        const newScrollLeft = scrollLeft + scrollVisibleWidth

        thumbRef.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        })

        if (newScrollLeft >= scrollContainerWidth - scrollVisibleWidth)
          setShowSlideNext(false)
        else setShowSlidePrev(true)
      }
    }
  }

  const slidePrev = () => {
    if (thumbnailRef?.current) {
      const thumbRef = thumbnailRef.current
      if (thumbnailDirection() === 'vertical') {
        const newScrollTop = scrollTop - scrollVisibleHeight
        thumbRef.scrollTo({
          top: newScrollTop,
          behavior: 'smooth'
        })
        if (newScrollTop <= 0) setShowSlidePrev(false)
        else setShowSlideNext(true)
      }
      if (thumbnailDirection() === 'horizontal') {
        const newScrollLeft = scrollLeft - scrollVisibleWidth
        thumbRef.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        })
        if (newScrollLeft <= 0) setShowSlidePrev(false)
        else setShowSlideNext(true)
      }
    }
  }

  const scrollReset = () => {
    if (thumbnailRef?.current) {
      const thumbRef = thumbnailRef.current
      thumbRef.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }

  // scroll event listener to set Thumbnail logic
  React.useEffect(() => {
    scrollReset()
    setShowSlidePrev(false)
    if (!thumbnailRef?.current) return
    const thumbRef = thumbnailRef.current
    const throttle = createThrottle(500)
    const callback = throttle(() => {
      const thumbRef = thumbnailRef.current
      if (!thumbRef) return
      setScrollTop(thumbRef.scrollTop)
      setScrollLeft(thumbRef.scrollLeft)
      setScrollVisibleHeight(thumbRef.clientHeight)
      setScrollVisibleWidth(thumbRef.clientWidth)
      setScrollContainerHeight(thumbRef.scrollHeight)
      setScrollContainerWidth(thumbRef.scrollWidth)
    })
    thumbRef.addEventListener('scroll', callback)
    callback()
    return () => {
      thumbRef.removeEventListener('scroll', callback)
    }
  }, [images])

  // show slidePrev logic with scroll event
  React.useEffect(() => {
    if (thumbnailRef?.current) {
      if (thumbnailDirection() === 'vertical') {
        if (scrollTop > 0) setShowSlidePrev(true)
        else setShowSlidePrev(false)
      }
      if (thumbnailDirection() === 'horizontal') {
        if (scrollLeft > 0) setShowSlidePrev(true)
        else setShowSlidePrev(false)
      }
    }
  }, [thumbnailRef, scrollLeft, scrollTop])

  // show slideNext logic with scroll event
  React.useEffect(() => {
    if (thumbnailRef?.current) {
      if (thumbnailDirection() === 'vertical') {
        if (scrollContainerHeight === scrollVisibleHeight)
          setShowSlideNext(false)
        else {
          // +1 because of inaccuracy scrollTop
          if (scrollContainerHeight - scrollVisibleHeight > scrollTop + 1)
            setShowSlideNext(true)
          else setShowSlideNext(false)
        }
      }
      if (thumbnailDirection() === 'horizontal') {
        // +1 because of inaccuracy scrollTop
        if (scrollContainerWidth - scrollVisibleWidth > scrollLeft + 1)
          setShowSlideNext(true)
        else setShowSlideNext(false)
      }
    }
  }, [
    images,
    scrollLeft,
    scrollTop,
    scrollContainerHeight,
    scrollContainerWidth,
    scrollVisibleHeight,
    scrollVisibleWidth
  ])

  const gallery: Gallery = {
    mainImage,
    thumbnails,
    index,
    slideNext,
    slidePrev,
    setIndex,
    showSlideNext,
    showSlidePrev,
    totalThumbs,
    thumbnailRef
  }
  return gallery
}
